import React, {useEffect, useState} from "react";
import {useFormikContext} from "formik";
import ElogSelect, {Option} from "../../common/form/ElogSelect";
import ElogInput from "../../common/form/ElogInput";
import {apiClient} from "../../../utils/ApiClient";

const currencyOptions: Option[] = [
    {value: 'EUR', label: 'EUR'},
    {value: 'USD', label: 'USD'},
    {value: 'SEK', label: 'SEK'},
];

interface ExchangeRate {
    rate: number;
    date: string;
}

interface BuyCurrencyInputProps {
    label: string;
    mandatory: boolean;
}

const toNumber = (value: unknown): number => parseFloat(String(value ?? '').replace(/\s/g, '').replace(',', '.'));

const formatDate = (date: string): string => date.split('-').reverse().join('.');

const BuyCurrencyInput: React.FC<BuyCurrencyInputProps> = ({label, mandatory}) => {
    const {values, initialValues, setFieldValue} = useFormikContext<any>();
    const currency: string = values?.buyCurrency || 'EUR';
    const original = toNumber(values?.buySumOriginal);

    const [latestRates, setLatestRates] = useState<Record<string, ExchangeRate>>({});
    const [error, setError] = useState('');

    const savedRate: ExchangeRate | null = toNumber(initialValues?.buyExchangeRate) > 0
        && initialValues?.buyCurrency === currency
        && toNumber(initialValues?.buySumOriginal) === original
        ? {rate: toNumber(initialValues.buyExchangeRate), date: String(initialValues.buyExchangeRateDate ?? '')}
        : null;

    const rate: ExchangeRate | null = currency === 'EUR' ? null : savedRate ?? latestRates[currency] ?? null;
    const needsLatestRate = currency !== 'EUR' && !savedRate && !latestRates[currency];

    useEffect(() => {
        if (!needsLatestRate) return;
        let cancelled = false;
        apiClient.get(`exchange-rates/${currency}`)
            .then(response => {
                if (cancelled) return;
                setLatestRates(previous => ({...previous, [currency]: {rate: Number(response.data.rate), date: response.data.date}}));
                setError('');
            })
            .catch(() => {
                if (!cancelled) setError(`Valuuta ${currency} kurssi ei õnnestunud laadida`);
            });
        return () => {
            cancelled = true;
        };
    }, [currency, needsLatestRate]);

    const rateValue = rate?.rate;
    const buySum = values?.buySum;

    useEffect(() => {
        if (!rateValue) return;
        const converted = Number.isFinite(original) ? (original / rateValue).toFixed(2) : '';
        if (converted === '' ? buySum !== '' : toNumber(buySum) !== Number(converted)) {
            setFieldValue('buySum', converted);
        }
    }, [rateValue, original, buySum, setFieldValue]);

    return (
        <>
            <ElogSelect label="Ostuarve valuuta" name="buyCurrency" options={currencyOptions}/>
            {currency !== 'EUR' && (
                <>
                    <ElogInput label={`Ostu summa (${currency})`} name="buySumOriginal" type="number" mandatory={true}/>
                    <tr>
                        <th>Kurss:</th>
                        <td>
                            {rate
                                ? `1 EUR = ${rate.rate.toFixed(4)} ${currency} (Euroopa Keskpank, ${formatDate(rate.date)})`
                                : (error ? <span style={{color: 'red'}}>{error}</span> : 'Laen kurssi…')}
                        </td>
                    </tr>
                </>
            )}
            <ElogInput label={label} name="buySum" type="number" unit="EUR" mandatory={mandatory} readOnly={currency !== 'EUR'}/>
        </>
    );
};

export default BuyCurrencyInput;
